<script lang="ts">
  import { onDestroy, onMount, type Snippet } from "svelte"
  import RegularHoursSection from "@kontextwerk/control-web-frame/components/availabilityPolicies/RegularHoursSection.svelte"
  import {
    copyMondayAvailabilityToWeekdays,
    normalizeAvailabilityPolicyConfig,
    validateAvailabilityPolicyConfig,
  } from "@kontextwerk/control-web-frame/logic/availabilityPolicies"
  import type { AvailabilityPolicyConfig } from "@kontextwerk/control-web-frame/types/availabilityPolicies"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { mdiContentCopy } from "@mdi/js"
  import {
    fetchCustomerAppointmentAvailability,
    fetchCustomers,
    updateCustomerAppointmentAvailability,
  } from "../../../../../../../api/kehrblatt/customer-registry/customers"
  import { fetchPropertyCustomers } from "../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import {
    formatPropertyCustomerRole,
    normalizePropertyCustomerRole,
  } from "../../../../_logic/customer-roles"
  import type { PlanningPreviewRow } from "../../_shared/_logic/table-contract"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Candidate = {
    customer: Customer
    link: PropertyCustomer
    label: string
    priority: number
    exactUnit: boolean
  }

  const {
    organizationId,
    row,
    initialCustomerId = null,
    onSaved = null,
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  } = $props<
    {
      organizationId: string
      row: PlanningPreviewRow
      initialCustomerId?: string | null
      onSaved?: ((detail: { customerId: string }) => void) | null
    } & OverlayHostProps
  >()

  const unitRolePriority = ["TENANT", "OWNER", "OTHER", "MANAGER", "BILLING_CONTACT"]
  const buildingRolePriority = ["MANAGER", "OWNER", "OTHER", "TENANT", "BILLING_CONTACT"]

  let loading = $state(false)
  let saving = $state(false)
  let error = $state<string | null>(null)
  let candidates = $state<Candidate[]>([])
  let selectedCustomerId = $state("")
  let draft = $state<AvailabilityPolicyConfig | null>(null)

  const sanitizeAppointmentAvailabilityConfig = (
    value?: AvailabilityPolicyConfig | null,
  ): AvailabilityPolicyConfig => ({
    ...normalizeAvailabilityPolicyConfig(value, { emptyDayDefaultMode: "OPEN" }),
    exclude_german_nationwide_holidays: false,
    closed_dates: [],
    closed_date_titles: {},
  })

  const hasWindows = (value?: AvailabilityPolicyConfig | null) =>
    Boolean(value?.hours?.some((day) => day.windows?.length))

  const normalizedDraft = $derived(sanitizeAppointmentAvailabilityConfig(draft))
  const hasAvailabilityWindows = $derived(hasWindows(normalizedDraft))
  const hasClosedDays = $derived(
    Boolean(normalizedDraft.hours?.some((day) => !day.windows?.length && day.mode === "CLOSED")),
  )
  const selectedOptions = $derived(
    candidates.map((entry) => ({
      name: entry.label,
      value: entry.customer.id,
    })),
  )

  const rolePriority = (role: string) => {
    const priority = row.unitId ? unitRolePriority : buildingRolePriority
    const index = priority.indexOf(role)

    return index >= 0 ? index : priority.length
  }

  const matchesRowScope = (link: PropertyCustomer) => {
    if (link.property_id !== row.propertyId) return false

    if (!row.unitId) return !link.property_unit_id

    return !link.property_unit_id || link.property_unit_id === row.unitId
  }

  const candidateLabel = (customer: Customer, link: PropertyCustomer) => {
    const role = formatPropertyCustomerRole(link.role)
    const scope = link.property_unit_id ? "Wohneinheit" : "Gebäudeeinheit"

    return `${customer.display_name || "Ansprechpartner"} - ${role} (${scope})`
  }

  const buildCandidates = (links: PropertyCustomer[], customers: Customer[]): Candidate[] => {
    const customerById = new Map(customers.map((customer) => [customer.id, customer]))

    return links
      .filter(matchesRowScope)
      .map((link) => {
        const customer = customerById.get(link.customer_id)

        if (!customer) return null

        const role = normalizePropertyCustomerRole(link.role)

        return {
          customer,
          link,
          label: candidateLabel(customer, link),
          priority: rolePriority(role),
          exactUnit: Boolean(row.unitId && link.property_unit_id === row.unitId),
        }
      })
      .filter((entry): entry is Candidate => Boolean(entry))
      .sort((left, right) => {
        if (left.exactUnit !== right.exactUnit) return left.exactUnit ? -1 : 1

        if (left.priority !== right.priority) return left.priority - right.priority

        return left.customer.display_name.localeCompare(right.customer.display_name, "de")
      })
  }

  const loadAvailability = async (customerId: string) => {
    if (!customerId) {
      draft = sanitizeAppointmentAvailabilityConfig(null)

      return
    }

    const response = await fetchCustomerAppointmentAvailability(customerId)
    draft = sanitizeAppointmentAvailabilityConfig(response.availability)
  }

  const selectCustomer = async (customerId: string) => {
    selectedCustomerId = customerId
    error = null
    loading = true
    try {
      await loadAvailability(customerId)
    } catch (loadError) {
      console.error("appointment_contact_availability_load_failed", loadError)
      error = "Die Verfügbarkeiten konnten nicht geladen werden."
    } finally {
      loading = false
    }
  }

  const loadCandidates = async () => {
    if (!organizationId || !row.propertyId) return

    loading = true
    error = null
    try {
      const links = await fetchPropertyCustomers({
        organizationId,
        propertyId: row.propertyId,
      })
      const customerIds = Array.from(new Set(links.map((link) => link.customer_id).filter(Boolean)))

      if (!customerIds.length) {
        candidates = []
        selectedCustomerId = ""
        draft = sanitizeAppointmentAvailabilityConfig(null)

        return
      }

      const customerResponse = await fetchCustomers({
        organizationId,
        customerIds,
        limit: customerIds.length,
      })
      candidates = buildCandidates(links, customerResponse.items)
      const preferredCustomerId = String(initialCustomerId ?? "").trim()
      const selectedCandidate =
        candidates.find((entry) => entry.customer.id === preferredCustomerId) ??
        candidates[0] ??
        null
      selectedCustomerId = selectedCandidate?.customer.id ?? ""

      if (selectedCustomerId) {
        await loadAvailability(selectedCustomerId)
      } else {
        draft = sanitizeAppointmentAvailabilityConfig(null)
      }
    } catch (loadError) {
      console.error("appointment_contact_availability_candidates_failed", loadError)
      error = "Die Ansprechpartner konnten nicht geladen werden."
    } finally {
      loading = false
    }
  }

  const saveAvailability = async () => {
    if (!selectedCustomerId || saving) return

    const normalized = sanitizeAppointmentAvailabilityConfig(draft)
    const validationError = validateAvailabilityPolicyConfig(normalized)

    if (validationError) {
      error = validationError

      return
    }

    saving = true
    error = null
    try {
      const response = await updateCustomerAppointmentAvailability(selectedCustomerId, {
        availability: normalized,
      })
      draft = sanitizeAppointmentAvailabilityConfig(response.availability)
      newNotification({ class: "success", html: "Verfügbarkeiten gespeichert." })
      onSaved?.({ customerId: selectedCustomerId })
      onremoveOverlay?.()
    } catch (saveError) {
      console.error("appointment_contact_availability_save_failed", saveError)
      error = "Die Verfügbarkeiten konnten nicht gespeichert werden."
    } finally {
      saving = false
    }
  }

  const copyMondayToWeekdays = () => {
    draft = sanitizeAppointmentAvailabilityConfig(copyMondayAvailabilityToWeekdays(normalizedDraft))
  }

  const close = () => {
    if (saving) return

    onremoveOverlay?.()
  }

  onMount(() => {
    setActionButtonSnippet?.(actionButtons)
    onshowOverlay?.()
    void loadCandidates()
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="contact-availability-overlay form">
  <ActionBox label="Ansprechpartner" darkMode={true}>
    <div class="availability-box-content">
      <p>
        Wählen Sie den Ansprechpartner, für den erlaubte Terminzeiten gepflegt werden sollen. Tage
        ohne Zeitfenster können als verfügbar oder nicht verfügbar markiert werden.
      </p>
      {#if !loading && !candidates.length}
        <p class="empty-copy">Für diese Zeile ist kein Ansprechpartner verknüpft.</p>
      {:else}
        <Input
          type="select"
          placeholder="Ansprechpartner"
          options={selectedOptions}
          bind:value={selectedCustomerId}
          disabled={loading || saving || !selectedOptions.length}
          brightBackground={true}
          onChange={() => void selectCustomer(selectedCustomerId)}
        />
      {/if}
    </div>
  </ActionBox>

  <ActionBox
    label="Erlaubte Terminzeiten"
    tag={hasAvailabilityWindows ? "Zeitfenster" : hasClosedDays ? "Individuell" : "Ganztägig"}
    tagColor={hasAvailabilityWindows ? "orange" : hasClosedDays ? "blue" : "green"}
    tagVariant="soft"
    darkMode={true}
    buttonSnippet={regularActionButtons}
  >
    <div class="availability-box-content">
      {#if loading}
        <p>Verfügbarkeiten werden geladen…</p>
      {:else if error}
        <p class="error-copy">{error}</p>
      {:else}
        <RegularHoursSection
          value={normalizedDraft}
          disabled={saving || !selectedCustomerId}
          emptyDayLabel="Ganztägig verfügbar"
          emptyDayDefaultMode="OPEN"
          emptyDayClosedLabel="Ganztägig geschlossen"
          emptyDayOpenLabel="Verfügbar"
          onChange={(value) => {
            draft = sanitizeAppointmentAvailabilityConfig(value)
          }}
        />
      {/if}
    </div>
  </ActionBox>
</div>

{#snippet regularActionButtons()}
  <CTA
    type="secondaryButton"
    content="Montag auf Werktage kopieren"
    icon={mdiContentCopy}
    small={true}
    darkMode={false}
    onClick={copyMondayToWeekdays}
    disabled={saving || loading || !selectedCustomerId}
  />
{/snippet}

{#snippet actionButtons()}
  <CTA
    type="primaryButton"
    content={saving ? "Speichert..." : "Speichern"}
    onClick={saveAvailability}
    disabled={saving || loading || !selectedCustomerId}
  />
{/snippet}

<style lang="less">
  .contact-availability-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 58rem;
  }

  .availability-box-content {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    color: var(--text-invers-100);

    :global(.regular-section) {
      color: var(--text-invers-100);
    }
  }

  .availability-box-content p {
    margin: 0;
    color: var(--text-invers-100);
    line-height: 1.45;
  }

  .empty-copy,
  .error-copy {
    padding-left: 0.8rem;
    border-left: 2px solid var(--primary-100);
  }

  .error-copy {
    border-left-color: var(--status-red-100, #b42318);
  }
</style>
